import './FilterPanel.css';
import 'react-datepicker/dist/react-datepicker.css';
import { MdClose as CloseIcon } from 'react-icons/lib/md';
import { t } from 'i18next';
import deepEqual from 'fast-deep-equal';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import React, { Component, createElement } from 'react';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import CallToAction from '../callToAction/CallToAction';
import {
  fetchingReportsData, updateFilter, filterUpdated, expandAddFilter, meteringApi,
} from '../../../reducer/actions';
import { filterSelector } from '../../../reducer/selectors/filterPanelSelector';
import DateRangeFilterForm from '../forms/dateRangeFilterForm/DateRangeFilterForm';
import Modal from '../../modal/Modal';
import MultiSelectFilterForm from '../forms/multiSelectFilterForm/MultiSelectFilterForm';
import SingleSelectFilterForm from '../forms/singleSelectFilterForm/SingleSelectFilterForm';
import SingleSelectFilterFormWithPairs from '../forms/singleSelectFilterFormWithPairs/SingleSelectFilterFormWithPairs';
import AutosuggestFilterForm from '../forms/autoSuggestFilterForm/AutoSuggestFilterForm';
import TextFilterForm from '../forms/textFilterForm/TextFilterForm';
import AddFilter from '../addFilter/AddFilter';
import DefaultReport from '../defaultReports/DefaultReports';
import { ellipsis } from '../../../helpers/formatter';

// ==================================
// COMPONENTS
// ==================================

const Filter = withRouter(({
  close, filter, open,
}) => (
  <div className={`Filter ${filter.name}`}
    onClick={open}
    role="presentation"
  >
    {filter.isEllipsis
      ? (
        <div>
          <span dangerouslySetInnerHTML={{
            __html: t(`filters.filter.${filter.name}`, {
              value: Array.isArray(filter.value) ? ellipsis(filter.value.join(', '), 15)
                : ellipsis(filter.value, 15),
            }),
          }} data-for="oemName" data-tip={filter.value}
          />
          <ReactTooltip className="tooltip"
            effect="solid"
            id="oemName"
            place="right"
          />
        </div>
      )
      : (
        <span dangerouslySetInnerHTML={{
          __html: t(`filters.filter.${filter.name}`, {
            value: Array.isArray(filter.value) ? filter.value.join(', ') : filter.value,
          }),
        }}
        />
      )
    }
    {filter.required
      ? (
        <img role="presentation"
          src="closeLight.svg"
          alt={t('filter.reset')}
          className="resetIco"
          onClick={(e) => { e.stopPropagation(); close(); }}
        />
      )
      : (
        <CloseIcon size="21"
          onClick={(e) => { e.stopPropagation(); close(); }}
        />
      )
    }
  </div>
));


// =========================================
// FILTERS
// =========================================

const textFilter = (name, inputRequired = false) => ({
  component: TextFilterForm(name, inputRequired),
  getValue: currentFilters => currentFilters[name],
  name,
  updateValue: (value, currentFilters) => {
    const next = {
      ...currentFilters,
    };
    delete next[name];
    return value === 'RESET' ? ({
      ...next,
      [name]: undefined,
    }) : ({
      ...next,
      [name]: value,
    });
  },
});

const singleSelectFilter = (name, listValues, required = false, defaultValue) => ({
  component: SingleSelectFilterForm(name, listValues, defaultValue),
  getValue: currentFilters => currentFilters[name],
  name,
  required,
  updateValue: (value, currentFilters) => {
    const next = {
      ...currentFilters,
    };
    delete next[name];
    return value === 'RESET' ? ({
      ...next,
      [name]: defaultValue,
    }) : ({
      ...next,
      [name]: value,
    });
  },
});

const singleSelectFilterWithPairs = (name, listValues, required = false, defaultValue) => ({
  component: SingleSelectFilterFormWithPairs(name, listValues, defaultValue),
  getValue: currentFilters => listValues[currentFilters[name]],
  name,
  required,
  updateValue: (value, currentFilters) => {
    const validValue = value && listValues[value];

    // return value;
    const next = {
      ...currentFilters,
    };
    delete next[name];
    return value === 'RESET' ? ({
      ...next,
      [name]: defaultValue,
    }) : ({
      ...next,
      [name]: validValue ? value : currentFilters[name],
    });
  },
});

const autoSuggestFilter = (name, listValues, required = false, defaultValue, isEllipsis = true) => ({
  component: AutosuggestFilterForm(name, listValues),
  getValue: (currentFilters) => {
    if (listValues && (! listValues.hasOwnProperty('message'))) {
      const currentFilter = listValues.find(value => value.id === currentFilters[name]);
      return currentFilter ? currentFilter.name : currentFilters[name];
    }
    return 'loading...';
  },
  isEllipsis,
  name,
  required,
  updateValue: (value, currentFilters) => {
    const next = {
      ...currentFilters,
    };
    delete next[name];
    return value === 'RESET' ? ({
      ...next,
      [name]: defaultValue.id,
    }) : ({
      ...next,
      [name]: value,
    });
  },
});

const multiSelectFilter = (name, listValues, required = false, defaultValue) => ({
  component: MultiSelectFilterForm(name, listValues),
  getValue: currentFilters => currentFilters[name],
  name,
  required,
  updateValue: (value, currentFilters) => {
    const next = {
      ...currentFilters,
    };
    delete next[name];
    if (value === 'RESET' || value.length === 0) {
      return ({
        ...next,
        [name]: defaultValue,
      });
      // eslint-disable-next-line no-else-return
    } else {
      return ({
        ...next,
        [name]: value,
      });
    }
  },
});

const yearsList = () => {
  const years = [{values: []}];
  const startYear = 2015;
  const currentYear = new Date().getFullYear();
  for (let year = currentYear; year >= startYear; year--) {
    years[0].values.push(year);
  }
  return years;
};

// ---------------------------------------------------
// DEFAULT VALUES FOR FILTER
// ---------------------------------------------------

export const default_start_date = moment.utc(moment().subtract(1, 'month')).format('YYYY-MM-DD');
export const default_end_date = moment.utc(moment()
  .subtract(1, 'day'))
  .format('YYYY-MM-DD');
export const default_left_start_date = moment.utc(moment()
  .subtract(1, 'year'))
  .format('YYYY-MM-DD');
export const default_left_end_date = moment.utc(moment()
  .subtract(6, 'month')
  .subtract(1, 'day'))
  .format('YYYY-MM-DD');
export const default_right_start_date = moment.utc(moment()
  .subtract(6, 'month'))
  .format('YYYY-MM-DD');
export const default_right_end_date = moment.utc(moment()
  .subtract(1, 'day'))
  .format('YYYY-MM-DD');

export const default_oem_model = 'All';
export const default_aggregate_by = 'month';
export const default_year = moment.utc(moment()).format('YYYY');
export const oem_id = data => autoSuggestFilter('oem_id', data, true, {
  id: '0dfc7900',
  name: 'Ayla',
});
export const oem_model = data => multiSelectFilter('oem_model', data, true, default_oem_model);
export const dsn = (inputRequired = false) => textFilter('dsn', inputRequired);
export const dp_name = data => singleSelectFilter('dp_name', data, true, '');
export const notification_type = data => singleSelectFilter('notification_type', data, true, 'All');
export const sw_version = data => multiSelectFilter('sw_version', data, true, 'All');
export const dp_value = textFilter('dp_value');
export const property_name = textFilter('property_name');
export const display_name = textFilter('display_name');
export const region_name = (data) => {
  if (process.env.REACT_APP_IS_VPC_ENVIRNOMENT === 'true') {
    return singleSelectFilterWithPairs('region_name', data, true, 'US-VPC');
  }
  if (process.env.REACT_APP_IS_US_ENVIRNOMENT === 'true') {
    return singleSelectFilterWithPairs('region_name', data, true, 'US-FIELD');
  }
  if (process.env.REACT_APP_IS_EU_ENVIRNOMENT === 'true') {
    return singleSelectFilterWithPairs('region_name', data, true, 'EU');
  }
  return singleSelectFilterWithPairs('region_name', data, true, 'US-FIELD');
};
export const year = singleSelectFilter('year', yearsList(), true, default_year);
export const duration = data => singleSelectFilter('duration', data, true, 'Monthly');
export const period = data => singleSelectFilterWithPairs('period', data, true, '1');
export const dsns = data => singleSelectFilterWithPairs('dsns', data, true, 'All');
export const billing_term = {
  component: DateRangeFilterForm,
  getValue: ({ billing_start_date, billing_end_date, }) => {
    if (billing_start_date && billing_end_date) {
      return {
        end_date: billing_end_date,
        start_date: billing_start_date,
      };
    }
    return null;
  },
  name: 'billing_term',
  required: true,
  updateValue: (value, currentFilters) => (value === 'RESET' ? ({
    ...currentFilters,
    billing_end_date: default_end_date,
    billing_start_date: default_start_date,
  }) : ({
    ...currentFilters,
    billing_end_date: value.end_date,
    billing_start_date: value.start_date
  })),
};

export const date_range = {
  component: DateRangeFilterForm,
  getValue: ({
    start_date, end_date, left_start_date, left_end_date, right_start_date, right_end_date,
  }, name) => {
    if (start_date && end_date) {
      switch (name) {
        case 'left':
          return {
            end_date: left_end_date,
            start_date: left_start_date,
          };

        case 'right': return {
          end_date: right_end_date,
          start_date: right_start_date,
        };
        default:
          return {
            end_date,
            start_date,
          };
      }
    }
    return null;
  },
  name: 'date_range',
  required: true,
  updateValue: (value, currentFilters, name) => {
    const next = {
      ...currentFilters,
    };
    delete next.start_date;
    delete next.end_date;
    if (name === 'left' || name === 'right') {
      const newvalue = {
        ...value,
      };
      newvalue[`${name}_start_date`] = newvalue.start_date;
      newvalue[`${name}_end_date`] = newvalue.end_date;
      delete newvalue.start_date;
      delete newvalue.end_date;
      return value === 'RESET' ? ({
        ...next,
        [`${name}_start_date`]: default_start_date,
        [`${name}_end_date`]: default_end_date,
      }) : ({
        ...next,
        ...newvalue,
      });
    }

    return value === 'RESET' ? ({
      ...next,
      end_date: default_end_date,
      start_date: default_start_date,
    }) : ({
      ...next,
      ...value,
    });
  },
};

// -----------------------------------------
// PANEL
// -----------------------------------------

class FilterPanel extends Component {
  us_field_dp_metering_oems
      = process.env.REACT_APP_USFIELD_DP_METERING_OEMS ? process.env.REACT_APP_USFIELD_DP_METERING_OEMS.split(", ") : '';
  eu_field_dp_metering_oems
      = process.env.REACT_APP_EU_DP_METERING_OEMS ? process.env.REACT_APP_EU_DP_METERING_OEMS.split(", ") : '';

  constructor(props) {
    super(props);
    const { defaultOpenModal } = this.props;
    this.state = {
      currentFilter: defaultOpenModal,
    };
    this.expandAddFilter = (expanded) => {
      const { disabled, dispatch } = this.props;
      if (! disabled) {
        dispatch(expandAddFilter(expanded));
      }
    };
    this.openModal = (currentFilter) => {
      const { disabled } = this.props;
      if (! disabled) {
        this.setState({
          currentFilter,
        });
      }
    };
    this.closeModal = () => this.setState({
      currentFilter: undefined,
    });
    this.getReports = () => {
      const { disabled, dispatch, currentFilters} = this.props;
      if (((currentFilters.region_name === 'US-FIELD') && (this.us_field_dp_metering_oems.includes(currentFilters.oem_id)))
          || ((currentFilters.region_name === 'EU') && (this.eu_field_dp_metering_oems.includes(currentFilters.oem_id)))
          || (((currentFilters.region_name === 'US-VPC') || (currentFilters.region_name === 'EU')) && (currentFilters.oem_id === '39a9391a'))) {
        dispatch(meteringApi(true));
      } else {
        dispatch(meteringApi(false));
      }
      if (! disabled) dispatch(fetchingReportsData(true));
    };
    this.dropFilter = (filter) => {
      const {
        name, currentFilters, dispatch,
      } = this.props;
      this.closeModal();
      const updatedFilters = filter.updateValue('RESET', currentFilters, name);
      if (! deepEqual(currentFilters, updatedFilters)) {
        dispatch(updateFilter(updatedFilters));
        dispatch(filterUpdated(true));
      }
    };
    this.pushFilter = (filter) => {
      const {
        name, currentFilters, dispatch,
      } = this.props;
      this.closeModal();
      const updatedFilters = filter.updateValue(filter.value, currentFilters, name);
      if (! deepEqual(currentFilters, updatedFilters)) {
        dispatch(updateFilter(updatedFilters));
        dispatch(filterUpdated(true));
      }
    };
    this.updateCurrent = (next) => {
      const { currentFilter } = this.state;
      return this.setState({
        currentFilter: {
          ...currentFilter,
          value: next,
        },
      });
    };
  }

  render() {
    const {
      filters, currentFilters, name, applyFilters = true, disabled = false,
      addFilterExpanded, isFilterUpdated, hideDefaultReports
    } = this.props;
    const { currentFilter } = this.state;
    const displayedFilters = filters.filter(f => f && !! f.getValue(currentFilters, name))
      .map(f => ({
        ...f,
        value: f.getValue(currentFilters, name),
      }));
    const addableFilters = filters.filter(f => f && ! f.getValue(currentFilters))
      .map(f => ({
        ...f,
        open: () => this.openModal(f),
      }));
    const modalContent = currentFilter && createElement(currentFilter.component, {
      current: currentFilter.value,
      name,
      pushFilter: () => this.pushFilter(currentFilter),
      updateCurrent: this.updateCurrent,
    });
    const { defaultReports } = this.props;


    return (
      <div className={`FilterPanel ${disabled && ! currentFilter ? 'disableFilters' : ''}`}>
        <div className="currentFilters">
          {displayedFilters.map(filter => (
            <Filter close={() => this.dropFilter(filter)}
              filter={filter}
              key={filter.name}
              open={() => this.openModal(filter)}
            />
          ))}

        </div>

        {addableFilters.length > 0 && (
          <span className="addFilter">
            <AddFilter filters={addableFilters}
              disabled={disabled}
              setExpanded={expanded => this.expandAddFilter(expanded)}
              expanded={addFilterExpanded}
            />

          </span>
        )}
        {applyFilters && (
          <span className="apply"
            onClick={() => this.getReports()}
            role="presentation"
            style={{
              animation: isFilterUpdated ? 'greenPulse 2s infinite' : 'none',
            }}
          >
            {isFilterUpdated && <CallToAction />}
            { filters[1].name === 'year' ? (<a>
              {disabled ? t('filters.generating') : t('filters.generateReport')}
            </a>) : (<a>
              {disabled ? t('filters.updating') : t('filters.updateReport')}
            </a>)}
          </span>
        )}
        { applyFilters && hideDefaultReports ? null : <DefaultReport data={defaultReports} /> }


        {!! currentFilter
          && (
            <Modal title={`${t('filters.title')}: ${t(`filters.label.${currentFilter.name}`)}`}
              close={this.closeModal}
            >
              {modalContent}
              <div className="actions">
                <a onClick={this.closeModal}
                  tabIndex="0"
                  role="button"
                >
                  {t('filters.cancel')}
                </a>
                <a onClick={() => this.dropFilter(currentFilter)}
                  role="presentation"
                >
                  {currentFilter.required ? t('filters.reset') : t('filters.remove')}
                </a>
                <a onClick={() => this.pushFilter(currentFilter)}
                  role="presentation"
                  className="submit"
                >
                  {t('filters.submit')}

                </a>
              </div>
            </Modal>
          )
        }

      </div>
    );
  }
}


export default withRouter(connect(s => filterSelector(s))(FilterPanel));
